import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Car,
  AlertCircle,
  Users2,
  Route,
  Clock,
  DollarSign,
  Target,
  Zap,
  MapPin,
  Star,
  CheckCircle,
  XCircle,
  Timer
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <TrendingUp className="text-blue-500" size={20} /> : <TrendingDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const RideshareAnalyticsReport = () => {
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("totalRides");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    driverAnalytics: true,
    passengerAnalytics: true,
    routeAnalytics: true,
    performanceMetrics: true
  });

  // Data states
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Fetch rideshare data
  const fetchRideshareData = async () => {
    setLoading(true);
    try {
      // Fetch real rideshare data from backend
      const [rideshareTripsRes, rideshareRequestsRes, driversRes, customersRes] = await Promise.all([
        fetch(`${API_URL}/rideshare/`),                    // All rideshare trips
        fetch(`${API_URL}/rideshare/requests/all`),       // All rideshare requests
        fetch(`${API_URL}/driver/`),                      // Drivers
        fetch(`${API_URL}/customerdetails/`)               // Customers
      ]);

      const [rideshareTripsData, rideshareRequestsData, driversData, customersData] = await Promise.all([
        rideshareTripsRes.json(),
        rideshareRequestsRes.json(),
        driversRes.json(),
        customersRes.json()
      ]);

      console.log("🔍 Enhanced Rideshare Analytics Debug:");
      console.log("rideshareTrips:", rideshareTripsData);
      console.log("rideshareRequests:", rideshareRequestsData);
      console.log("drivers:", driversData);
      console.log("customers with new analytics:", customersData);

      setRideshareTrips(rideshareTripsData || []);
      setRideshareRequests(rideshareRequestsData || []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
    } catch (error) {
      console.error("Error fetching rideshare data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRideshareData();
    const interval = setInterval(fetchRideshareData, 30000);
    return () => clearInterval(interval);
  }, [timeFilter]);

  // Calculate rideshare analytics
  const rideshareAnalytics = useMemo(() => {
    console.log("🔍 Rideshare Analytics Debug:");
    console.log("rideshareTrips:", rideshareTrips);
    console.log("rideshareRequests:", rideshareRequests);
    console.log("drivers:", drivers);
    console.log("customers:", customers);
    
    if (!rideshareTrips || !Array.isArray(rideshareTrips) || rideshareTrips.length === 0) {
      console.log("❌ No rideshare trips data available");
      return {
        totalTrips: 0,
        totalRequests: 0,
        completedTrips: 0,
        activeTrips: 0,
        cancelledTrips: 0,
        acceptedRequests: 0,
        pendingRequests: 0,
        completedRequests: 0,
        cancelledRequests: 0,
        averageResponseTime: 0,
        totalRevenue: 0,
        averagePricePerSeat: 0,
        occupancyRate: 0,
        driverParticipation: 0,
        passengerParticipation: 0
      };
    }

    const totalTrips = Array.isArray(rideshareTrips) ? rideshareTrips.length : 0;
    const totalRequests = Array.isArray(rideshareRequests) ? rideshareRequests.length : 0;
    
    console.log("📊 Total trips:", totalTrips);
    console.log("📊 Total requests:", totalRequests);
    console.log("📊 rideshareRequests type:", typeof rideshareRequests, Array.isArray(rideshareRequests));
    
    if (totalTrips > 0) {
      console.log("🚗 Sample trip data:", rideshareTrips[0]);
      console.log("🚗 All trip statuses:", rideshareTrips.map(t => t.status));
    }
    
    const completedTrips = Array.isArray(rideshareTrips) ? rideshareTrips.filter(trip => trip.status === 'Completed').length : 0;
    const activeTrips = Array.isArray(rideshareTrips) ? rideshareTrips.filter(trip => trip.status === 'Created Shared Ride Request').length : 0;
    const cancelledTrips = Array.isArray(rideshareTrips) ? rideshareTrips.filter(trip => trip.status === 'Cancelled').length : 0;
    
    console.log("✅ Completed trips:", completedTrips);
    console.log("🔄 Active trips:", activeTrips);
    console.log("❌ Cancelled trips:", cancelledTrips);
    
    const acceptedRequests = Array.isArray(rideshareRequests) ? rideshareRequests.filter(req => req.status === 'Created Shared Ride Request').length : 0;
    const pendingRequests = Array.isArray(rideshareRequests) ? rideshareRequests.filter(req => req.status === 'Created Shared Ride Request').length : 0;
    const completedRequests = Array.isArray(rideshareRequests) ? rideshareRequests.filter(req => req.status === 'Completed').length : 0;
    const cancelledRequests = Array.isArray(rideshareRequests) ? rideshareRequests.filter(req => req.status === 'Cancelled').length : 0;
    
    // Calculate average response time from request data (if available)
    const averageResponseTime = (Array.isArray(rideshareRequests) && rideshareRequests.length > 0) 
      ? rideshareRequests.reduce((sum, req) => sum + (req.response_time || 0), 0) / rideshareRequests.length 
      : 0;
    
    const totalRevenue = Array.isArray(rideshareTrips) ? rideshareTrips.reduce((sum, trip) => sum + (parseFloat(trip.revenue) || 0), 0) : 0;
    const averagePricePerSeat = Array.isArray(rideshareTrips) && rideshareTrips.length > 0
      ? rideshareTrips.reduce((sum, trip) => sum + (parseFloat(trip.price_per_seat) || 0), 0) / rideshareTrips.length
      : 0;
    
    const totalSeats = Array.isArray(rideshareTrips) ? rideshareTrips.reduce((sum, trip) => sum + (parseInt(trip.total_seats) || 0), 0) : 0;
    const occupiedSeats = Array.isArray(rideshareTrips) ? rideshareTrips.reduce((sum, trip) => sum + ((parseInt(trip.total_seats) || 0) - (parseInt(trip.available_seats) || 0)), 0) : 0;
    const occupancyRate = totalSeats > 0 ? (occupiedSeats / totalSeats) * 100 : 0;
    
    console.log("💰 Revenue calculation:");
    console.log("  - Total Revenue:", totalRevenue);
    console.log("  - Average Price/Seat:", averagePricePerSeat);
    console.log("  - Trip revenues:", rideshareTrips.map(t => ({ status: t.status, revenue: t.revenue, price_per_seat: t.price_per_seat })));
    
    console.log("🪑 Occupancy calculation:");
    console.log("  - Total Seats:", totalSeats);
    console.log("  - Occupied Seats:", occupiedSeats);
    console.log("  - Occupancy Rate:", occupancyRate);
    console.log("  - Seat details:", rideshareTrips.map(t => ({ status: t.status, total_seats: t.total_seats, available_seats: t.available_seats })));
    
    const driverParticipation = Array.isArray(drivers) && drivers.length > 0 ? (rideshareTrips.length / drivers.length) * 100 : 0;
    const passengerParticipation = Array.isArray(customers) && customers.length > 0 ? (rideshareRequests.length / customers.length) * 100 : 0;
    
    console.log("👥 Participation:");
    console.log("  - Driver Participation:", driverParticipation, `(${rideshareTrips.length}/${drivers.length})`);
    console.log("  - Passenger Participation:", passengerParticipation, `(${rideshareRequests.length}/${customers.length})`);

    const result = {
      totalTrips,
      totalRequests,
      completedTrips,
      activeTrips,
      cancelledTrips,
      acceptedRequests,
      pendingRequests,
      completedRequests,
      cancelledRequests,
      averageResponseTime,
      totalRevenue,
      averagePricePerSeat,
      occupancyRate,
      driverParticipation,
      passengerParticipation
    };
    
    console.log("📈 Final Analytics Result:", result);
    return result;
  }, [rideshareTrips, rideshareRequests, drivers, customers]);

  // Filter and sort data
  const filteredTrips = useMemo(() => {
    if (!rideshareTrips || !Array.isArray(rideshareTrips)) return [];
    
    return rideshareTrips.filter(trip => 
      (trip.route && trip.route.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (trip.driver_id && trip.driver_id.toString().includes(searchTerm.toLowerCase())) ||
      (trip.status && trip.status.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [rideshareTrips, searchTerm]);

  const sortedTrips = useMemo(() => {
    if (!filteredTrips || !Array.isArray(filteredTrips)) return [];
    
    return [...filteredTrips].sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'revenue':
          aValue = parseFloat(a.revenue) || 0;
          bValue = parseFloat(b.revenue) || 0;
          break;
        case 'route':
          aValue = a.route || '';
          bValue = b.route || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          aValue = parseFloat(a.revenue) || 0;
          bValue = parseFloat(b.revenue) || 0;
      }
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });
  }, [filteredTrips, sortBy, sortOrder]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const exportData = (data, filename) => {
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <Car className="mr-3 text-blue-600" />
            Rideshare Analytics Report
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Comprehensive analysis of rideshare operations, driver participation, and passenger engagement
          </p>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Understanding Rideshare Analytics">
          <p><strong>Key Metrics:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Total Trips:</strong> Number of rideshare trips created by drivers</li>
            <li><strong>Total Requests:</strong> Number of passenger requests to join rideshare trips</li>
            <li><strong>Occupancy Rate:</strong> Percentage of seats filled in rideshare vehicles</li>
            <li><strong>Driver Participation:</strong> Ratio of drivers actively offering rideshare trips</li>
            <li><strong>Passenger Participation:</strong> Ratio of customers using rideshare services</li>
            <li><strong>Average Response Time:</strong> Time taken for drivers to accept passenger requests</li>
          </ul>
          <p className="mt-2"><strong>Business Value:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Optimize routes and pricing based on demand patterns</li>
            <li>Identify high-performing drivers and routes</li>
            <li>Improve passenger experience through faster response times</li>
            <li>Maximize revenue through better occupancy rates</li>
            <li>Expand rideshare network to underserved areas</li>
          </ul>
        </ReportTooltip>

        {/* Time Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-2 mb-2 sm:mb-0">
              <Calendar className="text-blue-600" size={20} />
              <span className="font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All Time" },
                { value: "today", label: "Today" },
                { value: "7days", label: "Past 7 Days" },
                { value: "30days", label: "Past 30 Days" }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setTimeFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeFilter === filter.value
                      ? "bg-blue-600 text-white shadow-lg transform scale-105"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Rideshare Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {rideshareAnalytics.totalTrips}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {rideshareAnalytics.activeTrips} active
                </p>
              </div>
              <Route className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Passenger Requests</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {rideshareAnalytics.totalRequests}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {rideshareAnalytics.acceptedRequests} accepted
                </p>
              </div>
              <Users2 className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Occupancy Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {rideshareAnalytics.occupancyRate.toFixed(1)}%
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Seats filled
                </p>
              </div>
              <Target className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${rideshareAnalytics.totalRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  Avg: ${rideshareAnalytics.averagePricePerSeat.toFixed(2)}/seat
                </p>
              </div>
              <DollarSign className="text-yellow-500" size={24} />
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Activity className="mr-2 text-blue-600" />
            Rideshare Status Breakdown
          </h2>
          
          {/* Driver Trips Status */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">Driver Trips Status</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {rideshareAnalytics.completedTrips}
                </div>
                <div className="text-sm text-green-800 dark:text-green-200">Completed</div>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {rideshareAnalytics.activeTrips}
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-200">Created Shared Ride Request</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {rideshareAnalytics.cancelledTrips}
                </div>
                <div className="text-sm text-red-800 dark:text-red-200">Cancelled</div>
              </div>
            </div>
          </div>
          
          {/* Passenger Requests Status */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">Passenger Requests Status</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {rideshareAnalytics.completedRequests}
                </div>
                <div className="text-sm text-green-800 dark:text-green-200">Completed</div>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {rideshareAnalytics.acceptedRequests}
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-200">Created Shared Ride Request</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {rideshareAnalytics.cancelledRequests}
                </div>
                <div className="text-sm text-red-800 dark:text-red-200">Cancelled</div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Activity className="mr-2 text-blue-600" />
              Performance Metrics
            </h2>
            <button
              onClick={() => toggleSection('performanceMetrics')}
              className="text-blue-600 hover:text-blue-800"
            >
              {expandedSections.performanceMetrics ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {expandedSections.performanceMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Driver Participation</span>
                  <Users className="text-blue-500" size={16} />
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(rideshareAnalytics.driverParticipation, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {rideshareAnalytics.driverParticipation.toFixed(1)}% of drivers
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Passenger Participation</span>
                  <Users2 className="text-purple-500" size={16} />
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(rideshareAnalytics.passengerParticipation, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {rideshareAnalytics.passengerParticipation.toFixed(1)}% of customers
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Avg Response Time</span>
                  <Timer className="text-green-500" size={16} />
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${Math.min((rideshareAnalytics.averageResponseTime / 300) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {rideshareAnalytics.averageResponseTime.toFixed(0)} seconds
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Rideshare Trips Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Route className="mr-2 text-blue-600" />
              Rideshare Trips
            </h2>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Search trips..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="revenue">Revenue</option>
                <option value="route">Route</option>
                <option value="status">Status</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="px-3 py-1 bg-gray-200 rounded-lg text-sm hover:bg-gray-300"
              >
                {sortOrder === 'desc' ? '↓' : '↑'}
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3">Route</th>
                  <th className="px-4 py-3">Driver ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Seats</th>
                  <th className="px-4 py-3">Price/Seat</th>
                  <th className="px-4 py-3">Revenue</th>
                  <th className="px-4 py-3">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {sortedTrips.map((trip) => (
                  <tr key={trip.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium">{trip.route}</td>
                    <td className="px-4 py-3 font-mono text-xs">{trip.driver_id}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        trip.status === 'Created Shared Ride Request' ? 'bg-blue-100 text-blue-800' :
                        trip.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        trip.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {trip.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {trip.total_seats - trip.available_seats}/{trip.total_seats}
                    </td>
                    <td className="px-4 py-3">${trip.price_per_seat}</td>
                    <td className="px-4 py-3 font-semibold">${trip.revenue}</td>
                    <td className="px-4 py-3">
                      <div className="w-12 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${((trip.total_seats - trip.available_seats) / trip.total_seats) * 100}%` }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Debug Information</h3>
          <div className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
            <p>Rideshare Trips: {Array.isArray(rideshareTrips) ? `Array(${rideshareTrips.length})` : 'Not an array'}</p>
            <p>Rideshare Requests: {Array.isArray(rideshareRequests) ? `Array(${rideshareRequests.length})` : 'Not an array'}</p>
            <p>Drivers: {Array.isArray(drivers) ? `Array(${drivers.length})` : 'Not an array'}</p>
            <p>Customers: {Array.isArray(customers) ? `Array(${customers.length})` : 'Not an array'}</p>
            <p>Time Filter: {timeFilter}</p>
            {rideshareTrips.length > 0 && (
              <>
                <p>Trip status values found: {[...new Set(rideshareTrips.map(trip => trip.status).filter(Boolean))].join(', ')}</p>
                <p>Trip status counts: {JSON.stringify([...new Set(rideshareTrips.map(trip => trip.status).filter(Boolean))].reduce((acc, status) => {
                  acc[status] = rideshareTrips.filter(trip => trip.status === status).length;
                  return acc;
                }, {}), null, 2)}</p>
              </>
            )}
            {rideshareRequests.length > 0 && (
              <>
                <p>Request status values found: {[...new Set(rideshareRequests.map(req => req.status).filter(Boolean))].join(', ')}</p>
                <p>Request status counts: {JSON.stringify([...new Set(rideshareRequests.map(req => req.status).filter(Boolean))].reduce((acc, status) => {
                  acc[status] = rideshareRequests.filter(req => req.status === status).length;
                  return acc;
                }, {}), null, 2)}</p>
              </>
            )}
            <p>✅ Using real rideshare data from backend endpoints</p>
            <p>📡 Endpoints: /rideshare/, /rideshare/requests/all, /rideshare/analytics/summary</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RideshareAnalyticsReport;
